import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AuthShell, TextInput, toast } from "@etherisc/ui-kit";
import { useAuth } from "../hooks/useAuth";
import { GoogleOAuthButton } from "../components/GoogleOAuthButton";
import { PrimaryButton } from "../components/buttons";
import type { SignupCredentials } from "../types/Auth";

const signupSchema = z.object({
  name: z.string()
    .min(2, "Name must be at least 2 characters")
    .max(50, "Name must be less than 50 characters"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/\d/, "Password must contain at least one number"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"],
});

export function SignupPage() {
  const navigate = useNavigate();
  const { register, isAuthenticated, isLoading, error, clearError } = useAuth();
  const showError = (message: string, description?: string) => toast.error(message, { description });
  const showSuccess = (message: string, description?: string) => toast.success(message, { description });

  const {
    register: registerField,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<SignupCredentials>({
    resolver: zodResolver(signupSchema),
  });

  const password = watch("password");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error) {
      showError("Registration Failed", error);
      clearError();
    }
  }, [error, showError, clearError]);

  const onSubmit = async (data: SignupCredentials) => {
    try {
      await register(data);
      showSuccess("Registration Successful", "Welcome! You have been logged in automatically.");
    } catch {
      // Error handling is done through the useAuth hook
    }
  };

  const handleGoogleSuccess = () => {
    // OAuth success is handled by the GoogleOAuthButton component
    // User will be redirected by the useEffect above
  };

  const handleGoogleError = () => {
    // Error handling is done by the GoogleOAuthButton component
  };

  // Password strength indicator
  const getPasswordStrength = (pwd: string): { score: number; label: string; color: string } => {
    if (!pwd) return { score: 0, label: "", color: "" };
    
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[a-z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++; // Special characters

    if (score <= 1) return { score, label: "Weak", color: "text-red-500" };
    if (score <= 3) return { score, label: "Medium", color: "text-yellow-500" };
    return { score, label: "Strong", color: "text-green-500" };
  };

  const passwordStrength = getPasswordStrength(password || "");

  return (
    <AuthShell>
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Create Account</h1>
          <p className="text-gray-600 mt-2">Sign up to get started</p>
        </div>

        {/* Google OAuth Button */}
        <div className="space-y-4">
          <GoogleOAuthButton
            theme="filled_blue"
            size="large"
            text="signup_with"
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-2 text-gray-500">Or sign up with email</span>
            </div>
          </div>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <TextInput
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            error={errors.name?.message}
            {...registerField("name")}
          />

          <TextInput
            label="Email"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            {...registerField("email")}
          />

          <div className="space-y-2">
            <TextInput
              label="Password"
              type="password"
              placeholder="Create a password"
              error={errors.password?.message}
              {...registerField("password")}
            />
            
            {password && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span>Password strength:</span>
                  <span className={passwordStrength.color}>{passwordStrength.label}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      passwordStrength.score <= 1 ? 'bg-red-500' :
                      passwordStrength.score <= 3 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Password must contain:</p>
                  <ul className="list-disc list-inside space-y-0.5 ml-2">
                    <li className={password.length >= 8 ? 'text-green-600' : 'text-gray-400'}>
                      At least 8 characters
                    </li>
                    <li className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                      One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                      One lowercase letter
                    </li>
                    <li className={/\d/.test(password) ? 'text-green-600' : 'text-gray-400'}>
                      One number
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          <TextInput
            label="Confirm Password"
            type="password"
            placeholder="Confirm your password"
            error={errors.confirmPassword?.message}
            {...registerField("confirmPassword")}
          />

          <PrimaryButton type="submit" fullWidth disabled={isLoading}>
            {isLoading ? "Creating Account..." : "Create Account"}
          </PrimaryButton>
        </form>

        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-600 hover:text-blue-800 font-medium">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </AuthShell>
  );
} 